import { ApplicationError } from "../../models/errors/application-error";
import IOrderService from "./order-service-interface";
import { ApplicationOrderList } from "../../models/order/application-order-list";
import { ApplicationOrder } from "../../models/order/application-order";
import { ApplicationOrderLineList } from "../../models/order/application-order-line-list";
import { ApplicationApprovalStatus } from "../../models/order/application-approval-status";
import { mapStringToEnum } from "../../helpers/enum-helper";
import { ApplicationOrderLine } from "../../models/order/application-order-line";
import client from "../../external/open-api";
import { ApplicationOrderLineUpdate } from "../../models/order/application-order-line-update";
import { ApplicationSizeUnit } from "../../models/product/application-size-unit";
import { ApplicationOrderStatus } from "../../models/order/application-order-status";
import { ApplicationOrderLineSplit } from "../../models/order/application-order-line-split";
import { ApplicationOrderSummary } from "../../models/order/application-order-summary";
import { components } from "../../external/schema/order-link-api-schema";
import BaseService from "../base-service";
import { ApplicationLastOrdered } from "../../models/order/application-last-ordered";
export default class OrderService extends BaseService implements IOrderService {

  async create(basketId: number): Promise<ApplicationOrder[]> {
    const { data, error } = await this.withRetry(() => client.POST("/v{version}/api/order", {
      params: {
        path: { version: "1" },
      },
      body: {
        basketId,
      },
    }));

    if (!data || error) {
      throw new ApplicationError("Api call failed creating order");
    }

    const response = await this.handleResponse(data, "Failed to create order");
    return response!.map(this.formatOrder);
  }

  async get(
    branchId: number,
    currentPosition: number,
    fetchNext: number,
    searchText: string,
    status: ApplicationOrderStatus,
  ): Promise<ApplicationOrderList> {
    const { data, error } = await this.withRetry(() => client.GET(
      "/v{version}/api/order/branch/{branchId}",
      {
        params: {
          path: { version: "1", branchId }, // Path parameter
          query: {
            "Paging.CurrentPosition": currentPosition,
            "Paging.FetchNext": fetchNext,
            "Paging.FetchAll": false,
            "SearchText": searchText,
            "OrderStatus": status
          },
        },
      }
    ));

    if (!data || error) {
      throw new ApplicationError("Api call failed fetching orders");
    }

    const response = await this.handleResponse(data, "Failed to fetch orders");

    const orders = response.orders?.map(this.formatOrder);

    return {
      orders: orders ?? [],
      count: response?.count || 0,
      currentPosition: response?.currentPosition || 0,
    };
  }

  async getById(orderId: number): Promise<ApplicationOrder> {
    const { data, error } = await this.withRetry(() => client.GET(
      "/v{version}/api/order/{orderId}",
      {
        params: {
          path: { version: "1", orderId },
        },
      }
    ));

    if (!data || error) {
      throw new ApplicationError("Api call failed fetching order");
    }

    const response = await this.handleResponse(data, "Failed to fetch order");
    return this.formatOrder(response ?? {});
  }

  async updateOrderStatus(orderId: number, status: ApplicationOrderStatus): Promise<ApplicationOrder> {
    const { data, error } = await this.withRetry(() => client.PATCH(
      "/v{version}/api/order/{orderId}/updatestatus",
      {
        params: {
          path: { version: "1", orderId },
        },
        body: {
          approvalStatus: status
        }
      }
    ));

    if (!data || error) {
      throw new ApplicationError("Api call failed updating order");
    }

    const response = await this.handleResponse(data, "Failed to update order status");
    return this.formatOrder(response ?? {});
  }

  async updateOrder(
    orderId: number,
    orders: ApplicationOrderLineUpdate[],
    completeOrder: boolean,
  ): Promise<ApplicationOrderLineList> {
    const { data, error } = await this.withRetry(() => client.PATCH(
      "/v{version}/api/order/{orderId}/supplier",
      {
        params: {
          path: { version: "1", orderId },
          query: { completeOrder }
        },
        body: {
          orders,
        },
      }
    ));

    if (!data || error) {
      throw new ApplicationError("Api call failed updating order line");
    }

    const response = await this.handleResponse(
      data,
      "Failed to update order supplier"
    );

    const orderLines =
      response.orderLines?.map(this.mapOrderLine) ?? [];

    return {
      orderLines,
      count: response.count || 0,
      currentPosition: response.currentPosition || 0,
    };
  }


  async createSplit(orderId: number, orderLineId: number, split: ApplicationOrderLineSplit[]): Promise<ApplicationOrderLine> {
    const { data, error } = await this.withRetry(() => client.POST("/v{version}/api/order/{orderId}/orderline/{orderLineId}/split", {
      params: {
        path: { version: "1", orderId, orderLineId },
      },
      body: {
        splits: split
      }
    }));

    if (error) {
      throw new ApplicationError("Create split failed, please check if split is valid");
    }

    const response = await this.handleResponse(data, "Failed to fetch order");
    return this.mapOrderLine(response ?? {});
  }

  async getOrderPDF(orderId: number): Promise<Blob> {
    const { data, error, response } = await this.withRetry(() => client.GET(
      "/v{version}/api/order/{orderId}/pdf",
      {
        params: {
          path: { version: "1", orderId },
        },
      }
    ));

    if (response?.status === 204) {
      throw new ApplicationError("No order PDF found");
    }

    if (!data?.data?.file || error) {
      throw new ApplicationError("Api call failed fetching order PDF");
    }

    // Safely decode the base64 file content
    const byteCharacters = atob(data.data.file);
    const byteNumbers = new Array(byteCharacters.length);

    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }

    const byteArray = new Uint8Array(byteNumbers);
    return new Blob([byteArray], { type: "application/pdf" });
  }

  async getOrderSummary(orderId: number): Promise<ApplicationOrderSummary> {
    const summaryResponse = await this.withRetry<{
      data?: {
        success?: boolean;
        data?: ApplicationOrderSummary;
        errorMessage?: string | null;
      };
      error?: unknown;
    }>(() =>
      (client as any).GET("/v{version}/api/order/summary/{orderId}", {
        params: {
          path: { version: "1", orderId },
        },
      })
    );

    const { data, error } = summaryResponse;

    if (!data || error) {
      throw new ApplicationError("Api call failed fetching order summary");
    }

    const response = data as {
      success?: boolean;
      data?: ApplicationOrderSummary;
      errorMessage?: string | null;
    };

    if (!response.success || !response.data) {
      throw new ApplicationError(
        response.errorMessage ?? "Failed to fetch order summary"
      );
    }

    return {
      lines: response.data.lines ?? [],
      vatBreakdown: response.data.vatBreakdown ?? [],
      net: response.data.net ?? 0,
      vat: response.data.vat ?? 0,
      gross: response.data.gross ?? 0,
    };
  }

  async getOrderLines(
    orderId: number,
    currentPosition: number,
    fetchNext: number,
    searchText: string
  ): Promise<ApplicationOrderLineList> {
    const { data, error } = await this.withRetry(() => client.GET(
      "/v{version}/api/order/{orderId}/orderlines",
      {
        params: {
          path: { version: "1", orderId },
          query: {
            "Paging.CurrentPosition": currentPosition,
            "Paging.FetchNext": fetchNext,
            "Paging.FetchAll": false,
            "SearchText": searchText
          },
        },
      }
    ));

    if (!data || error) {
      throw new ApplicationError("Api call failed fetching order");
    }

    const response = await this.handleResponse(
      data,
      "Failed to fetch order lines"
    );

    const orderLines =
      response.orderLines?.map(this.mapOrderLine) ?? [];
    return {
      orderLines,
      count: response.count || 0,
      currentPosition: response.currentPosition || 0,
    };
  }

  async validateBasket(basketId: number): Promise<string[]> {
    const { data, error } = await this.withRetry(() =>
      client.GET("/v{version}/api/order/validate/{basketId}", {
        params: {
          path: { version: "1", basketId },
        },
      })
    );

    if (error) {
      throw new ApplicationError("Failed to validate basket");
    }

    const response = data as { success?: boolean; data?: string[]; errorMessage?: string | null };
    if (response?.success === false) {
      throw new ApplicationError(response.errorMessage ?? "Failed to validate basket");
    }

    return response?.data ?? [];
  }

  async getCommonNotes(productId: number, supplierId: number): Promise<string[]> {
    const { data, error } = await this.withRetry(() =>
      client.GET("/v{version}/api/order/notes/{productId}/{supplierId}", {
        params: {
          path: { version: "1", productId, supplierId },
        },
      })
    );

    if (error) {
      throw new ApplicationError("Failed to get notes");
    }

    const response = data as { success?: boolean; data?: string[]; errorMessage?: string | null };
    if (response?.success === false) {
      throw new ApplicationError(response.errorMessage ?? "Failed to validate basket");
    }

    return response?.data ?? [];
  }


  private async handleResponse<T>(
    response: { success?: boolean; data?: T },
    errorMessage: string
  ): Promise<T> {
    if (!response.success) {
      throw new ApplicationError(errorMessage);
    }
    return response.data!;
  }

  private formatOrder(order: any): ApplicationOrder {
    return {
      id: order.id,
      branchId: order.branchId,
      lineCount: order.lineCount,
      displayName: `Order #${order.id}`,
      created: order.created,
      departmentName: order.departmentName,
      totalAmount: order.totalAmount,
      status: order.status
    };
  }
  private mapOrderLine = (orderLine: components["schemas"]["OrderLineDTO"]): ApplicationOrderLine => {
    return {
      id: orderLine.id ?? 0,
      orderId: orderLine.orderId ?? 0,
      productId: orderLine.productId ?? 0,
      productName: orderLine.productName ?? "",
      productDescription: orderLine.productDescription ?? "",
      productImage: orderLine.productImage ?? "",
      size: orderLine.size ?? 0,
      sizeUnit: mapStringToEnum(ApplicationSizeUnit, orderLine.sizeUnit ?? ""),
      quantity: orderLine.quantity ?? 0,
      currentStockLevel: orderLine?.currentStockLevel ?? 0,
      costPrice: orderLine?.costPrice ?? 0,
      notes: orderLine.notes ?? "",
      plu: orderLine.plu ?? "",
      openPricing: orderLine.openPricing ?? false,
      supplierId: orderLine?.supplierId ?? 0,
      supplierName: orderLine.supplierName ?? "",
      quantityReceived: orderLine.quantityReceived ?? 0,
      deliveryNotes: orderLine.deliveryNotes ?? "",
      approvalStatus: mapStringToEnum(
        ApplicationApprovalStatus,
        orderLine.approvalStatus ?? ""
      ),
      children: orderLine?.splits?.map(x => this.mapOrderLineSplits(x)) ?? [],
      lastOrdered: orderLine.lastOrdered ? this.mapLastOrdered(orderLine.lastOrdered!) : undefined
    };
  }

  private mapOrderLineSplits(split: components["schemas"]["OrderLineSplitDTO"]): ApplicationOrderLineSplit {
    return {
      id: split?.orderLineId ?? 0,
      orderLineId: split?.orderLineId ?? 0,
      supplierId: split?.supplierId ?? 0,
      supplierName: split?.supplierName ?? "",
      size: split?.size ?? 0,
      sizeUnit: mapStringToEnum(ApplicationSizeUnit, split.sizeUnit ?? ""),
      costPrice: split?.costPrice ?? 0,
      quantity: split?.quantity ?? 0,
      notes: split?.notes ?? "",
      quantityReceived: split?.quantityReceived ?? 0,
      deliveryNotes: split?.deliveryNotes ?? "",
    };
  }

  private mapLastOrdered(lastOrdered: components["schemas"]["LastOrderedDTO"]): ApplicationLastOrdered {
    return {
      date: new Date(lastOrdered.date!),
      quantity: lastOrdered.quantity!,
      cost: lastOrdered.cost!,
      unitCost: lastOrdered.unitCost!,
      currentStock: lastOrdered.currentStock! 
    }
  }
}
